// ONE ELEMENT, WHICHEVER PIECE YOU CLICKED.
//
// Tom's nested fix (2026-09-24) draws every nested sub-component again as
// its own instance row under its OWN uniqueId, on its own original
// fragment key - which is what restored instancing and took BA03's packs
// back down. The DATA stays as the owner decided: one commit row, one
// record, one take-off row per TOP-LEVEL family. A part is geometry, not
// an element.
//
// So a part's uid has no record behind it, and before this module
// clicking a nut showed NOTHING - it showed the nut before the merge and
// the hanger after it. This is the piece that makes a part behave like
// the thing it is part of.
//
// WHY A MAP AND NOT "MANY ROWS UNDER THE PARENT'S UID": Tom rejected that
// shape from his own audit and I confirmed it from mine - the viewer's
// single-active-instance invariant (InstanceStateApplicator) lets exactly
// one slot per uid render, because the octree routes a straddling
// instance into every tile it touches. Many geometries under one uid
// would draw one and silently zero the rest.
//
// WHY NOT DETECT A PART BY THE ABSENCE OF A RECORD: a uid with no record
// is also what a failed fetch, a chunked-tree miss and an older export
// look like. Parts are identified POSITIVELY, by the parent the exporter
// put on the row.

// part uid -> parent uid
const parentByPart = new Map();
// parent uid -> Set(part uids), the reverse, for acting on a whole family
const partsByParent = new Map();

/**
 * Record the parents carried by one tile's rows. Called as tiles load, so
 * what is known grows with what is on screen - which is correct: a part
 * in a tile nobody has loaded cannot be clicked either.
 */
export function registerNestedRows(rows) {
  if (!rows) return 0;
  let added = 0;
  for (const r of rows) {
    const part = r && (r.uniqueId || r.u);
    const parent = r && (r.parentUid || r.p);
    if (!part || !parent || part === parent) continue;
    if (parentByPart.get(part) === parent) continue;
    parentByPart.set(part, parent);
    let set = partsByParent.get(parent);
    if (!set) { set = new Set(); partsByParent.set(parent, set); }
    set.add(part);
    added += 1;
  }
  return added;
}

/** The parent of a nested part, or null when this uid is an element. */
export function parentOf(uid) {
  return parentByPart.get(uid) || null;
}

/**
 * The uid that IDENTIFIES what the user clicked: the parent when they hit
 * a part, the uid itself otherwise. One hop, not a chain - the exporter
 * flattens nested-of-nested onto the top-level parent, so a part's parent
 * is always an element.
 */
export function resolveToElement(uid) {
  return parentByPart.get(uid) || uid;
}

/**
 * Everything that must move together when an element is acted on: the
 * element and every part of it that has been seen. Given a part, it
 * returns the whole family, not just that part - hiding one nut of a
 * trapeze and leaving the other ninety-nine is not what anyone means.
 */
export function expandToFamily(uids) {
  const out = [];
  const seen = new Set();
  const push = (u) => { if (u && !seen.has(u)) { seen.add(u); out.push(u); } };
  for (const uid of uids || []) {
    const element = parentByPart.get(uid) || uid;
    push(element);
    const parts = partsByParent.get(element);
    if (parts) for (const p of parts) push(p);
  }
  return out;
}

/**
 * The parts of an element seen so far, or null when it has none (or is
 * itself a part). The live Set, not a copy: the applicator asks this once
 * per uid on every targeted update, and a copy per call is garbage it
 * does not need. Callers must not mutate it.
 */
export function partsOf(uid) {
  const set = partsByParent.get(uid);
  return set && set.size ? set : null;
}

/** True when `uid` is an element drawn (at least partly) as nested parts. */
export function hasParts(uid) {
  return !!partsOf(uid);
}

/** How much is known - for diagnostics and for the tests' match counts. */
export function nestedStats() {
  return { parts: parentByPart.size, parents: partsByParent.size };
}

/**
 * A new model in the same tab must not inherit the old one's parts: uids
 * are per-export and a stale map would resolve a click to an element that
 * is not there. Called from the loader's teardown.
 */
export function forgetNestedParts() {
  parentByPart.clear();
  partsByParent.clear();
}
